import { getDecimal, validateEmail } from "../global/utils";

export const feeFromName = (fees, name) => {
    if (fees && name) {
        const selectedFees = fees.filter(f => f.name === name);
        if (selectedFees) return selectedFees[0];
    }
    return null;
}

export const getFeeTotal = (selectedFees, selectedOption) => {
    let total = 0;
    if (selectedFees) {
        selectedFees.forEach(f => total += f.amount)
    }
    if (selectedOption && selectedOption.amount > 0) {
        total += selectedOption.amount;
    }
    total = parseFloat(getDecimal(total, false));
    return total;
}

// 3.3.1 wants an error to say which field it is about, and a single concatenated
// string cannot: the caller also has to know which control to mark aria-invalid
// and which one to move focus to. So validation returns one entry per problem,
// each carrying the id of the control at fault, and the summary Alert's text is
// just those messages joined. Same shape as completePayment/paymentFunctions.js
// (CPDEV-2777).
//
// Messages name the field with the words on its visible label, so what a screen
// reader reads back matches what the user is looking at. `field` is null when a
// problem is not attributable to one control - there is nothing to focus then,
// and the caller falls back to the Alert itself.
const fieldError = (field, message) => ({ field, message });

export const joinFieldErrors = (fieldErrors) => fieldErrors.map((e) => e.message).join('; ');

export const validateFeeInfoFields = (selectedFees, selectedOption, form, court) => {
    const errors = [];
    const totalAmt = getFeeTotal(selectedFees, selectedOption);
    if (!selectedFees || selectedFees.length < 1) {
        errors.push(fieldError(null, 'You must select at least one fee to pay'));
    } else if (totalAmt <= 0) {
        errors.push(fieldError(null, 'You must select items to pay with a value greater than zero'));
    } else if (court.maxTransactionLimit && court.maxTransactionLimit > 0 && totalAmt > court.maxTransactionLimit) {
        errors.push(fieldError(null,
            'The total amount of the selected fees exceeds the maximum payment amount allowed by ' + court.courtName));
    }
    // Each message uses the words on the field's own label. These read "Mailing
    // city" and "Mailing state" from ac1238d onwards, while the controls on screen
    // are labelled "City" and "State" - so the error named a field the user could
    // not find anywhere on the page (CPDEV-2789).
    [
        ['payorName', 'Your Name is required'],
        ['address', 'The Billing Address is required'],
        ['city', 'The City is required'],
        ['state', 'The State is required'],
        ['zip', 'The Billing Zip is required'],
    ].forEach(([field, message]) => {
        if (!form[field]) errors.push(fieldError(field, message));
    });
    if (form.email && !validateEmail(form.email)) {
        errors.push(fieldError('email', 'The Email Address is invalid'));
    }
    if (!form.email && !form.phone) {
        errors.push(fieldError('email',
            'You must enter either an Email Address or a Phone Number so we can contact you if there are questions about your request'));
    }
    return errors;
}

// The Add a Fee panel is its own form, and its only error used to be "the total
// amount must be greater than zero" - naming Total Amount, which is a computed
// read-only value the user cannot edit. Name the entry field actually at fault
// instead (CPDEV-2789, 3.3.1).
export const validateFeeEntry = (fee, payableFee) => {
    const errors = [];
    // A fixed fee amount comes from the court's configuration; a zero there is
    // not something the payer can correct, so do not point them at a read-only
    // field. Only an amount they were asked to enter is theirs to fix.
    if (payableFee && payableFee.allowAmountEntry && !(fee.itemAmount > 0)) {
        errors.push(fieldError('amount', 'The Enter Amount must be greater than zero'));
    }
    if (!(fee.quantity >= 1)) {
        errors.push(fieldError('quantity', 'The Quantity must be at least 1'));
    }
    if (errors.length < 1 && !(fee.amount > 0)) {
        errors.push(fieldError(null, 'To add a fee, the total amount must be greater than zero'));
    }
    return errors;
}

export const getPaymentRequest = (courtId, selectedFees, selectedOption, form, feeTotal, processingFee) => {
    const fees = [...selectedFees];

    if (selectedOption) {
        fees.push({
            cmsId: selectedOption.cmsId,
            name: selectedOption.name,
            amount: selectedOption.amount,
            quantity: 1,
            itemAmount: selectedOption.amount,
            deliveryMethod: true,
            comments: selectedOption.description
        });
    }
    return {
        courtId: courtId,
        paymentDescription: getFeesPaymentDescription(selectedFees),
        processingType: 'miscFees',
        partyName: form.partyName ? form.partyName : form.payorName,
        payorName: form.payorName,
        payorAddress: form.address,
        payorCity: form.city,
        payorState: form.state,
        billingZip: form.zip,
        email: form.email,
        phone: form.phone,
        totalAmount: parseFloat(getDecimal(feeTotal + processingFee, false)),
        courtAmount: feeTotal,
        feeAmount: processingFee,
        miscellaneousFees: fees
    };
}

export const getFeesPaymentDescription = (selectedFees) => {
    const fees = selectedFees.filter(f => !f.deliveryMethod);
    return `Miscellaneous fees: ${fees[0].name.substring(0, 25)}`;
}
